import React from 'react';
import { StyleSheet, View } from 'react-native';
import { isWebPlatform } from '../utils/webLayout';

type Props = {
  color: string;
  size?: number;
};

/**
 * Material-style edit/pencil icon (diagonal pencil + writing line).
 * Web uses the standard SVG paths; native uses the same silhouette.
 */
export function EditPencilIcon({ color, size = 22 }: Props) {
  if (isWebPlatform()) {
    return (
      <View
        accessibilityElementsHidden
        pointerEvents="none"
        style={{ width: size, height: size }}
      >
        {React.createElement(
          'svg',
          {
            width: size,
            height: size,
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: color,
            strokeWidth: 2,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            'aria-hidden': true,
          },
          React.createElement('path', { d: 'M12 20h9' }),
          React.createElement('path', {
            d: 'M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z',
          }),
        )}
      </View>
    );
  }

  const scale = size / 24;
  return (
    <View
      accessibilityElementsHidden
      pointerEvents="none"
      style={[styles.box, { width: size, height: size }]}
    >
      <View
        style={[
          styles.line,
          {
            backgroundColor: color,
            width: 11 * scale,
            height: 2 * scale,
            right: 1 * scale,
            bottom: 2 * scale,
          },
        ]}
      />
      <View
        style={[
          styles.body,
          {
            borderColor: color,
            width: 10 * scale,
            height: 14 * scale,
            borderWidth: Math.max(1.5, 2 * scale),
            borderRadius: 2 * scale,
          },
        ]}
      />
      <View
        style={[
          styles.eraser,
          {
            backgroundColor: color,
            width: 10 * scale,
            height: 3 * scale,
            top: 3 * scale,
          },
        ]}
      />
      <View
        style={[
          styles.tip,
          {
            borderTopColor: color,
            borderLeftWidth: 3.5 * scale,
            borderRightWidth: 3.5 * scale,
            borderTopWidth: 5 * scale,
            bottom: 1 * scale,
            left: 1.5 * scale,
          },
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    transform: [{ rotate: '-35deg' }],
  },
  eraser: {
    position: 'absolute',
    borderRadius: 1,
    transform: [{ rotate: '-35deg' }],
  },
  tip: {
    position: 'absolute',
    width: 0,
    height: 0,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    transform: [{ rotate: '-35deg' }],
  },
  line: {
    position: 'absolute',
    borderRadius: 1,
  },
});
